/* eslint-disable no-nested-ternary */
import { t } from "i18next";
import omitBy from "lodash/omitBy";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";
import { Trans } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";

import { AtiraDatePicker } from "../AtiraDatePicker";
import { formatDateTimeWithoutTimezone } from "../../utils/Date";
import { Drawer } from "../Drawer";
import { Flex } from "../Flex";
import { Input } from "../Input";
import { Text } from "../Text";
import { Textarea } from "../Textarea";
import { TaskPriority } from "../../model/entry/types/TaskPriority.enum";
import { UserRoles } from "../../model/user/types/UserRoles";
import { footerActions } from "../../redux/footer-daily-stats/footer-daily-stats.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { taskSliceSelectors } from "../../redux/task/task.selector";
import { taskActions } from "../../redux/task/task.slice";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { AtiraToast } from "../../utils/AtiraToast";
import { appSliceSelectors } from "../../redux/app/app.selector";
import { standardDate } from "../../utils/Date";
import { EntityAssignCheckbox } from "../EntityAssignCheckbox";
import { Radio, Rate } from "../../utils/local-antd";
import { SettingsSubRoutes } from "../../model/app/types/SettingsSubRoutes.enum";
import { isUndefined } from "lodash";
import { TaskKind } from "../../model/task/shared/TasksKind.enum";
import { GetTasksFilterDto } from "../../model/task/dto/GetTasksFilterDto";
import { UpdateTaskDto } from "../../model/task/dto/UpdateTaskDto";
import { ContactTask, OrphanTask } from "../../model/task/Task";
import { DrawerDefaultsExtra } from "../../routes/tasks/components/DrawerDefaultsExtra";
import { Lengths } from "../../model/shared/enum/Lengths.enum";
import { getTaskPriorityColor } from "../../utils/taskUtils";
import { useDrawersContext } from "../../routes/tasks/drawers-context";
import fetchAfterTaskAction from "./functions/fetchAfterTaskAction";
import { ImageUpload } from "../ImageUpload";
import { TestIDs } from "../../tests/types/testIds";
import { onSetTypedCRUDErrors } from "../../utils/onSetTypedCRUDErrors";

const StyledInput = styled(Input)`
  height: 2.5rem;
  border: 2px solid ${(props) => props.theme.main};
`;

const StyledRadio = styled(Radio.Button)<{ priority: TaskPriority }>`
  border: ${({ theme, priority }) =>
    `1px solid ${theme[getTaskPriorityColor(priority)]}`} !important;
  color: ${({ theme, priority }) =>
    theme[getTaskPriorityColor(priority)]} !important;

  &.ant-radio-button-wrapper-checked {
    background-color: ${({ theme, priority }) =>
      theme[getTaskPriorityColor(priority)]} !important;
    color: ${({ theme }) => theme.white} !important;
  }
  &.ant-radio-button-wrapper::before {
    display: none;
  }
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.lightMain};
  text-decoration: underline;
  text-underline-offset: 5px;

  &:hover {
    color: ${({ theme }) => theme.main};
  }
`;

const validateTaskName = (value?: string) => {
  if (!value || !value.trim() || value.trim().length < 3) {
    return t("task.drawer.error.short_name");
  }
};

export const TaskUpdateDrawer: React.FC = () => {
  const [taskUpdateLoading, setTaskUpdateLoading] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const tasksPage = useAppSelector(taskSliceSelectors.selectTasksPage);
  const tasksPageSize = useAppSelector(taskSliceSelectors.selectTasksPageSize);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const searchOptions = useAppSelector(appSliceSelectors.selectSearchOptions);
  const floatingTodayTasksVisible = useAppSelector(
    appSliceSelectors.floatingTodayTasksVisible,
  );

  const isOrganization = user?.roles?.includes(UserRoles.ORGANIZATION);

  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const {
    task,
    setTask,
    setEntry,
    kanbanCard,
    setKanbanCard,
    taskUpdateVisible,
    setTaskUpdateVisible,
  } = useDrawersContext();

  const { control, getValues, reset, setError, handleSubmit } =
    useForm<UpdateTaskDto>();

  const [watchedReminder, watchedDeadline] = useWatch({
    control,
    name: ["reminder", "deadline"],
  });
  const { getValues: contextGetValues } = useFormContext<GetTasksFilterDto>();

  const buildResetValues = useCallback(
    (): UpdateTaskDto => ({
      name: task?.name,
      reminder: task?.reminder
        ? (standardDate(task?.reminder, user, true, true) as string)
        : null,
      deadline: task?.deadline
        ? (standardDate(task?.deadline, user, true, true) as string)
        : null,
      description: task?.description || "",
      taskId: task?._id!,
      userId: user?._id!,
      ...(task?.kind && [TaskKind.CONTACT, TaskKind.ORPHAN].includes(task.kind)
        ? { priority: (task as ContactTask | OrphanTask)?.priority }
        : {}),
      ...(task?.kind === TaskKind.CONTACT
        ? {
            rating: (task as ContactTask)?.rating || 0,
            image: (task as ContactTask)?.image,
          }
        : {}),
      assigneeIds: task?.assigneeIds,
      done: task?.done,
      kind: task?.kind,
    }),
    [task, user],
  );

  const localOnClose = () => {
    setTaskUpdateVisible(false);
    reset(buildResetValues());
  };

  const onEdit = async () => {
    try {
      setTaskUpdateLoading(true);

      const dto = getValues();

      if (dto?.name?.trim() === task?.name?.trim()) {
        delete dto.name;
      }

      Object.assign(dto, {
        userId,
        taskId: task?._id,
        ...(dto.reminder
          ? { reminder: formatDateTimeWithoutTimezone(dto.reminder) }
          : {}),
        ...(dto.deadline
          ? { deadline: formatDateTimeWithoutTimezone(dto.deadline) }
          : {}),
      });

      await dispatch(
        taskActions.updateTask({
          ...omitBy(dto, isUndefined),
          kind: task?.kind,
        } as UpdateTaskDto),
      ).unwrap();

      await fetchAfterTaskAction({
        userId,
        task,
        entryId: task?.parentId!,
        taskId: task?._id,
        kanbanCardId: kanbanCard?._id,
        pathname,
        tasksPage,
        tasksPageSize,
        dispatch,
        setEntry,
        setTask,
        setKanbanCard,
        contextGetValues,
        searchKeyword: searchOptions?.keyword,
        isFloatingTodayTasksOpen: floatingTodayTasksVisible === true,
      });

      dispatch(footerActions.getFooterDailyStats({ userId }));
      AtiraToast.success(t("task.update.success"));

      localOnClose();
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setTaskUpdateLoading(false);
    }
  };

  useEffect(() => {
    if (taskUpdateVisible && task?._id) {
      reset(buildResetValues());
    }
  }, [taskUpdateVisible, buildResetValues]);

  if (!task) {
    return null;
  }

  return (
    <Drawer
      destroyOnHidden
      maskClosable={false}
      title={`${t("common.task_update")} - ${task?.name}`}
      open={taskUpdateVisible}
      onClose={localOnClose}
      size="large"
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onEdit)}
          onCancel={localOnClose}
          config={{
            loading: taskUpdateLoading,
            editMode: true,
          }}
          submitTestId={TestIDs.tasks.createEdit.drawer.button}
        />
      }
    >
      <Flex flexDirection="column" gap="m">
        <Controller
          control={control}
          name="name"
          rules={{
            required: {
              value: true,
              message: t("common.this_field_required"),
            },
            validate: (value?: string) => validateTaskName(value),
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledInput
              value={value}
              onChange={onChange}
              title={t("common.task_name")}
              required
              valid={!error}
              errorMessage={error?.message}
              maxLength={Lengths.DESCRIPTION}
              dataTestId={TestIDs.tasks.createEdit.drawer.name}
            />
          )}
        />

        <Controller
          control={control}
          name="reminder"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AtiraDatePicker
              onChange={onChange}
              value={value || null}
              title={t("common.remind_date")}
              showTime
              info={
                <Trans
                  i18nKey={"common.dateStoredByTimezone.note"}
                  components={[
                    <StyledLink
                      to={`/settings/${SettingsSubRoutes.NOTIFICATIONS}`}
                    >
                      {t("common.settings")}
                    </StyledLink>,
                  ]}
                />
              }
              bottomNote={t("common.sendReminder.note")}
              fromToday
              maxDateValue={watchedDeadline}
              dataTestId={TestIDs.tasks.createEdit.drawer.reminder}
              valid={!error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="deadline"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AtiraDatePicker
              value={value || null}
              onChange={onChange}
              title={t("common.deadline")}
              showTime
              fromToday
              minDateValue={watchedReminder}
              dataTestId={TestIDs.tasks.createEdit.drawer.deadline}
              valid={!error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field: { value, onChange } }) => (
            <Textarea
              rows={3}
              title={t("common.description")}
              value={value}
              onChange={onChange}
              maxLength={Lengths.LONG}
              dataTestId={TestIDs.tasks.createEdit.drawer.description}
            />
          )}
        />

        {isOrganization ? (
          <Controller
            control={control}
            name="assigneeIds"
            render={({ field: { value, onChange } }) => (
              <EntityAssignCheckbox value={value || []} onChange={onChange} />
            )}
          />
        ) : null}

        {[TaskKind.CONTACT, TaskKind.ORPHAN].includes(task?.kind!) && (
          <Flex flexDirection="column" gap="s">
            <Text>{t("common.priority")}</Text>
            <Controller
              control={control}
              name="priority"
              render={({ field: { value, onChange } }) => (
                <Radio.Group onChange={onChange} value={value}>
                  {Object.values(TaskPriority).map((p) => (
                    <StyledRadio
                      key={p}
                      value={p}
                      priority={p}
                      className={TestIDs.tasks.createEdit.drawer.priority(p)}
                    >
                      {t(`common.${p.toLowerCase()}`)}
                    </StyledRadio>
                  ))}
                </Radio.Group>
              )}
            />
          </Flex>
        )}

        {task?.kind === TaskKind.CONTACT ? (
          <Fragment>
            <Flex flexDirection="column" gap="s">
              <Text>{t("task.update.image.title")}</Text>
              <Controller
                control={control}
                name="image"
                render={({ field: { value, onChange } }) => (
                  <ImageUpload
                    image={value || ""}
                    onChange={(f) => onChange(f)}
                  />
                )}
              />
            </Flex>

            <Flex flexDirection="column" gap="s">
              <Text>{t("task.update.rating.title")}</Text>
              <Controller
                control={control}
                name="rating"
                render={({ field: { value, onChange } }) => (
                  <Rate
                    count={10}
                    value={value}
                    onChange={onChange}
                    className={TestIDs.tasks.createEdit.drawer.rating}
                  />
                )}
              />
            </Flex>
          </Fragment>
        ) : null}
      </Flex>
    </Drawer>
  );
};
